import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Child, ChildContract, Game, Group, ListResponseContract, Questionnaire, Test } from '@isophi/teachers-shared';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { environment } from '../../../environments/environment';
import { GameService } from './game.service';
import { GroupService } from './group.service';
import { QuestionnaireService } from './questionnaire.service';
import { TestService } from './test.service';


@Injectable({
  providedIn: 'root',
})
export class ChildService {
  private childrenByUuidsReload$ = new BehaviorSubject<void>(null);

  private childrenReload$ = new BehaviorSubject<void>(null);

  private children$ = this.childrenReload$.pipe(
    switchMap(() => this.children())
  )

  public constructor(
    private httpClient: HttpClient,
    private groupService: GroupService,
    private testService: TestService,
    private gameService: GameService,
    private questionnaireService: QuestionnaireService
  ) {}

  /**
   * Return list of children.
   */
  public getChildren(): Observable<Child[]> {
    return this.children$
  }

  public children(resultsCount = true): Observable<Child[]> {
    const extraFields = `group_uuids,is_connected_to_parent${resultsCount ? ',results_count&source=all' : ''}`;
    const url = `${environment.teacherAPI}/children/?extra_fields=${extraFields}`;
    const childrenData$ = this.httpClient.get<ListResponseContract<ChildContract>>(url);

    return combineLatest({
      childrenData: childrenData$,
      groups: this.groupService.groups$,
      games: this.gameService.games$,
      tests: this.testService.tests$,
      questionnaires: this.questionnaireService.questionnaires$
    }).pipe(
      map(({ childrenData, groups, games, tests, questionnaires }) =>
        childrenData.results.map((childData) => this.deserializeChildWithGroups(childData, groups, games, tests, questionnaires))
      ),
      shareReplay(1)
    );
  }

  public refreshChildList() {
    this.childrenReload$.next();
  }

  /**
   * Return list of children.
   */
  public getChildrenByUuids(uuids: string[]): Observable<Child[]> {
    const extraFields = 'group_uuids,is_connected_to_parent';
    const url = `${environment.teacherAPI}/children/`;
    const params = new HttpParams().set('extra_fields', extraFields).set('uuid__in', uuids.join(','));

    const childrenData$ = this.httpClient.get<ListResponseContract<ChildContract>>(url, { params });

    return this.childrenByUuidsReload$.pipe(
      distinctUntilChanged(),
      switchMap(() =>
        combineLatest({
          childrenData: childrenData$,
          groups: this.groupService.groups$,
          games: this.gameService.games$,
          tests: this.testService.tests$,
          questionnaires: this.questionnaireService.questionnaires$
        }).pipe(
          map(({ childrenData, groups, games, tests, questionnaires }) =>
            childrenData.results.map((childData) => this.deserializeChildWithGroups(childData, groups, games, tests, questionnaires))
          )
        )
      ),
      shareReplay(1)
    )
  }

  /**
   * Return single child.
   *
   * @param uuid
   */
  public getChild(childUuid: string): Observable<Child> {
    const extraFields = 'group_uuids,is_connected_to_parent';
    const url = `${environment.teacherAPI}/children/${childUuid}/`;
    const params = new HttpParams().set('extra_fields', extraFields);

    const childData$ = this.httpClient.get<ChildContract>(url, { params });

    return combineLatest({
      childData: childData$,
      groups: this.groupService.groups$,
    }).pipe(
      map((data) => {
        const child = Child.deserialize(data.childData);
        this.setChildToGroups(child, data.groups, data.childData.group_uuids);
        return child;
      }),
      shareReplay(1)
    );
  }

  public update(child: Child): Observable<ChildContract> {
    const url = `${environment.teacherAPI}/children/${child.uuid}/`;

    return this.httpClient.patch<ChildContract>(url, child.serializeNew(true));
  }

  public create(child: Child): Observable<ChildContract> {
    const url = `${environment.teacherAPI}/children/`;

    const data = child.serializeNew(true);
    data.uuid = uuid();
    return this.httpClient.post<ChildContract>(url, data);
  }

  public delete(child: Child): Observable<void> {
    const url = `${environment.teacherAPI}/children/${child.uuid}/`;

    return this.httpClient.delete<void>(url);
  }

  /**
   * Connect Group and Child models.
   *
   * @param child
   * @param groups
   * @param groupUuids
   */
  public setChildToGroups(child: Child, groups: Group[], groupUuids: string[]): void {
    for (const groupUuid of groupUuids) {
      const group = groups.find((g) => g.uuid === groupUuid);
      if (!group) continue;
      group.addChild(child);
      child.addGroup(group);
    }
  }

  /**
   * Connect Group and Child models just for archive.
   *
   * @param child
   * @param groups
   * @param groupUuids
   */
  public setChildToGroupsArchive(child: Child, groups: Group[], groupUuids: string[]): void {
    for (const groupUuid of groupUuids) {
      const group = groups.find((g) => g.uuid === groupUuid);
      if (!group) continue;
      child.addGroup(group);
    }
  }

  /**
   * disconnect Group and Child models.
   *
   * @param child
   * @param groups
   * @param groups
   */
  public removeChildFromGroups(child: Child, groups: Group[], childGroups: Group[]): void {
    const groupUuids = childGroups.map((val) => val.uuid);
    for (const groupUuid of groupUuids) {
      const group = groups.find((g) => g.uuid === groupUuid);
      if (!group) continue;
      group.removeChild(child);
    }
  }

  public bulkUpdateGroups(listOfObjects: { uuid: string; set_groups: string[] }[]): Observable<void> {
    const url = `${environment.teacherAPI}/children/bulk-update/`;

    return this.httpClient.put<void>(url, listOfObjects);
  }

  private deserializeChildWithGroups(
    childData: ChildContract,
    groups: Group[],
    games: Game[],
    tests: Test[],
    questionnaires: Questionnaire[]
  ) {
    const child = Child.deserialize(childData);
    this.setChildToGroups(child, groups, childData.group_uuids);
    child.initTestResultsCount(childData.results_count, games, tests, questionnaires);
    return child;
  }
}
